import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import SignUpPresenter from './SignUpPresenter';
import {
  useRegisterMutation,
  useRegisterQuotationUserMutation,
  RegisterCapaUserInput,
  useRequestEmailVerificationCodeMutation,
  CapaServiceType,
  CapaErrorCode,
  CapaQuotationUserStatus,
  RegisterQuotationUserInput,
  CapaQuotationUserBizType,
  useVerifyCompanyRegistrationNumberQuery,
  useFetchMyQuotationUserLazyQuery,
  useUpdateAmbassadorSignUpPageViewCountByIpMutation,
  CapaQuotationUserType,
  CapaInflowRoute
} from '@generated/graphql';
import useClient from '@hooks/useClient';
import { useSnackbar } from '@components/Provider';
import { Helmet } from 'react-helmet';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import AppConfig from '@config';
import CommonLayout from '@routes/Common/CommonLayout';
import jwtDecode from '@utils/jwtDecode';
import { getUTMInfo } from '@utils/storage';

interface TermsAndServiceState {
  serviceAgree: boolean;
  privacyAgree: boolean;
  paymentAgree: boolean;
  promotionAgree: boolean;
}

const initialAgreeState: TermsAndServiceState = {
  serviceAgree: false,
  privacyAgree: false,
  paymentAgree: false,
  promotionAgree: false
};

const SignUpContainer: React.FC = () => {
  const [address, setAddress] = useState<{ full: string; short: string }>({
    full: '',
    short: ''
  });
  const [bizType, setBizType] = useState<CapaQuotationUserBizType>(
    CapaQuotationUserBizType.Biz
  );
  const { enqueueSnackbar } = useSnackbar();
  const { clientState, onUpdateToken, onUpdateClient } = useClient();
  const history = useHistory();
  const [agreeState, setAgreeState] = useState<TermsAndServiceState>(
    initialAgreeState
  );
  const { onInsertUserActionLogOne } = useUserActionLog();
  const { capaFriendsId } = useParams<{ capaFriendsId: string }>();
  const [
    updataViewCount
  ] = useUpdateAmbassadorSignUpPageViewCountByIpMutation();
  const [register] = useRegisterMutation();
  const [registerQuatationUser] = useRegisterQuotationUserMutation();
  const [
    requestEmailVerificationCode
  ] = useRequestEmailVerificationCodeMutation();
  const [
    fetchMyQuotationUser,
    myQuotationUserQuery
  ] = useFetchMyQuotationUserLazyQuery({
    fetchPolicy: 'network-only'
  });
  const {
    refetch: verifyCompanyRegistrationNumber
  } = useVerifyCompanyRegistrationNumberQuery({
    skip: true
  });
  const [accountType, setAccountType] = useState<CapaServiceType>(
    CapaServiceType.Quotation
  );

  const formProps = useForm<RegisterQuotationUserInput & RegisterCapaUserInput>(
    {
      mode: 'onChange',
      reValidateMode: 'onSubmit',
      defaultValues: {
        email: ''
      }
    }
  );

  const { setError, getValues } = formProps;

  const checkRegistrationNumber = useCallback(async () => {
    const companyRegistrationNumber = getValues('companyRegistrationNumber');
    if (companyRegistrationNumber) {
      const verifyResponse = await verifyCompanyRegistrationNumber({
        input: { companyRegistrationNumber }
      });

      if (
        verifyResponse &&
        verifyResponse.data.verifyCompanyRegistrationNumber.result
      ) {
        switch (verifyResponse.data.verifyCompanyRegistrationNumber.result) {
          case CapaErrorCode.Success:
            return true;
          case CapaErrorCode.DuplicateCompanyRegistrationNo:
            return true;
          case CapaErrorCode.InavlidCompanyRegistrationNo:
            return setError('companyRegistrationNumber', {
              message: '등록되지 않은 사업자등록번호 입니다.'
            });
          case CapaErrorCode.ParmeterValidationError:
            return setError('companyRegistrationNumber', {
              message: '잘못된 형식입니다.'
            });
          default:
            return setError('companyRegistrationNumber', {
              message: '사업자등록번호 조회에 실패했습니다.'
            });
        }
      }

      return setError('companyRegistrationNumber', {
        message: '사업자등록번호 조회에 실패했습니다.'
      });
    } else {
      return true;
    }
  }, [getValues, setError, verifyCompanyRegistrationNumber]);

  const onRegisterQuotationUser = async (req: RegisterQuotationUserInput) => {
    const response = await registerQuatationUser({
      variables: { input: { ...req } }
    }).catch((e) => {
      return { data: null };
    });

    if (
      response &&
      response.data?.registerQuotationUser.result === CapaErrorCode.Success
    ) {
      onUpdateToken({
        accessToken: response.data?.registerQuotationUser.data?.accessToken,
        refreshToken: response.data?.registerQuotationUser.data?.refreshToken
      });
    }
  };

  const onClickAuthButton = async (
    email: string,
    service: CapaServiceType,
    callback: () => void
  ) => {
    onInsertUserActionLogOne(
      Enum_Action_Button_Enum.Button_SignUp_SendAuthEmail,
      CapaActivityType.ButtonClick
    );

    const { data: requestResult } = await requestEmailVerificationCode({
      variables: { email, service }
    });
    switch (
      requestResult &&
      requestResult?.requestEmailVerificationCode.result
    ) {
      case CapaErrorCode.Success:
        enqueueSnackbar('인증메일이 발송되었습니다.', {
          variant: 'success'
        });
        callback();
        break;
      case CapaErrorCode.DuplicateEmail:
        formProps.setError('email', {
          message: '이미 가입된 이메일입니다.'
        });
        break;
      default:
        enqueueSnackbar('인증메일 발송에 실패했습니다.', {
          variant: 'error'
        });
    }
  };

  const onSubmit = async (
    input: RegisterQuotationUserInput & RegisterCapaUserInput
  ) => {
    if (address.full && address.short) {
      input.shortAddress = address.short;
    }
    input.serviceAgree = agreeState.serviceAgree;
    input.privacyAgree = agreeState.privacyAgree;
    input.promotionAgree = agreeState.promotionAgree;
    input.paymentAgree = agreeState.paymentAgree;
    input.service = accountType;
    input.bizType = bizType;
    if (input.companyRegistrationNumber?.length === 0) {
      input.companyRegistrationNumber = undefined;
    }

    const {
      email,
      code,
      password,
      privacyAgree,
      promotionAgree,
      paymentAgree,
      serviceAgree,
      inflowRoute,
      service,
      referralUserId,
      referralCompany,
      ...registerQuotationUserInput
    } = input;

    let registerInput: any = {
      email,
      code,
      password,
      passwordConfirm: password,
      privacyAgree,
      promotionAgree,
      paymentAgree,
      serviceAgree,
      inflowRoute,
      service
    };

    // 지인 및 기관 추천 직접 입력으로 가입인 경우
    if (referralCompany) {
      registerInput = { ...registerInput, referralCompany: referralCompany };
    }

    // 캐파 프렌즈 링크로 가입하는 경우
    if (capaFriendsId) {
      registerInput = {
        ...registerInput,
        inflowRoute: CapaInflowRoute.Recommendation,
        ambassadorId: capaFriendsId
      };
    }

    const verifyRegistrationNumberResult = await checkRegistrationNumber();
    if (!verifyRegistrationNumberResult) return;

    register({
      variables: {
        input: {
          ...registerInput,
          referralUserId: referralUserId
            ? (referralUserId as string)
            : undefined,
          registTbUtmInfo: getUTMInfo()
        }
      }
    }).then(async (result) => {
      const { Success, ParmeterValidationError } = CapaErrorCode;
      const { QuotationPartner } = CapaServiceType;

      switch (result.data?.register.result) {
        case Success: {
          const { accessToken, refreshToken } =
            result?.data?.register?.data || {};
          onUpdateToken({
            accessToken: accessToken as string,
            refreshToken: refreshToken as string
          });

          if (input.service === QuotationPartner) {
            window.location.href = `${AppConfig.partnersUrl}/auth/?accessToken=${accessToken}&refreshToken=${refreshToken}&signUp=true`;
            return;
          } else {
            return await onRegisterQuotationUser(registerQuotationUserInput);
          }
        }
        case ParmeterValidationError: {
          formProps.setError('password', {
            message: '영문+숫자 혼용 8글자 이상으로 입력해주세요.'
          });
          break;
        }
        default: {
          enqueueSnackbar(`회원가입 실패`, { variant: 'error' });
          break;
        }
      }
    });
  };

  /** 이미 로그인 되어있는 유저를 대시보드로 */
  useEffect(() => {
    if (clientState.status === CapaQuotationUserStatus.Registered) {
      history.replace('/dashboard');
    }
  }, [clientState, history]);

  /** 회원가입시 필수정보입력도 함께 완료한 유저정보 fetch */
  useEffect(() => {
    const { accessToken } = clientState ?? {};
    if (accessToken) {
      const { scopes } = jwtDecode() as {
        scopes: CapaServiceType[];
      };

      if (scopes.includes(CapaServiceType.Quotation)) {
        fetchMyQuotationUser();
      }
    }
  }, [clientState, fetchMyQuotationUser, history]);

  useEffect(() => {
    if (
      myQuotationUserQuery.data?.fetchMyQuotationUser?.result ===
      CapaErrorCode.Success
    ) {
      onUpdateClient(
        myQuotationUserQuery?.data?.fetchMyQuotationUser?.data ?? {}
      );

      history.push('/dashboard', {
        fromSignUp: true,
        hasRequiredInfo: true
      });
    }
  }, [myQuotationUserQuery, history, onUpdateClient]);

  useEffect(() => {
    if (address.full && address.short) {
      formProps.setValue('fullAddress', address.full);
      formProps.trigger('fullAddress');
    }
    //eslint-disable-next-line
  }, [address, formProps.setValue, formProps.trigger]);

  useEffect(() => {
    if (capaFriendsId) {
      try {
        updataViewCount({
          variables: {
            input: {
              ambassadorId: capaFriendsId,
              userType: CapaQuotationUserType.Client
            }
          }
        });
      } catch {
        enqueueSnackbar(`View Count Error`, { variant: 'error' });
      }
    }
    // eslint-disable-next-line
  }, [capaFriendsId]);

  return (
    <>
      <Helmet>
        <meta
          property="og:title"
          content={'제조견적비교 서비스 캐파에 초대합니다'}
        />
        <meta
          property="og:image"
          content={process.env.PUBLIC_URL + '/CAPA_REFERRAL_OGI.png'}
        />
        <meta
          property="og:description"
          content={
            '지금 캐파 가입하고 친구 초대하면 아메리카노 쿠폰을 최대 5장 받을 수 있어요.'
          }
        />
      </Helmet>
      {capaFriendsId ? (
        <>
          <FormProvider {...formProps}>
            <SignUpPresenter
              onClickAuthButton={onClickAuthButton}
              onSubmit={onSubmit}
              bizType={bizType}
              setBizType={(bizType: CapaQuotationUserBizType) =>
                setBizType(bizType)
              }
              setAddress={setAddress}
              agreeState={agreeState}
              setAgreeState={setAgreeState}
              accountType={accountType}
              setAccountType={setAccountType}
            />
          </FormProvider>
        </>
      ) : (
        <CommonLayout gnbTabs={<></>} fullHeight>
          <FormProvider {...formProps}>
            <SignUpPresenter
              onClickAuthButton={onClickAuthButton}
              onSubmit={onSubmit}
              bizType={bizType}
              setBizType={(bizType: CapaQuotationUserBizType) =>
                setBizType(bizType)
              }
              setAddress={setAddress}
              agreeState={agreeState}
              setAgreeState={setAgreeState}
              accountType={accountType}
              setAccountType={setAccountType}
            />
          </FormProvider>
        </CommonLayout>
      )}
    </>
  );
};

export default SignUpContainer;
